// ADR 2.10.26f §2 — the client half of roadmap 2.10.26b D3: one flow, two
// editors. The wire form is Backend 2.10.26e §2's; `builderApi.ts` closes it
// at the seam through `narrowDefinition` below.
//
// The client checks only what the engine needs in order to run — a known
// kind, unique handles, edges between real nodes, no cycle. Node, edge, script
// and size limits are the Backend's (`builder_flow_too_large`): a second copy
// of those numbers is how two layers start disagreeing. The exceptions are the
// per-value rules Backend 8.10.26e §7 asks the web to show (ADR 8.10.26f §3),
// which live in `dsl/params.ts`.
//
// ADR 8.10.26f §5: schema 1 or 2 is read, and every definition in memory is
// schema 2, so the editors always write 2.

import type { FeatureFlags } from '../../api/auth'
import { MAX_PARAMS, narrowParamValues, PARAM_NAME, type ParamValue, type ParamValues } from './dsl/params'

export type NodeKind = 'client' | 'secret' | 'webhook' | 'app'

export const NODE_KINDS: readonly NodeKind[] = ['client', 'secret', 'webhook', 'app']

export interface FlowNode {
  id: string
  kind: NodeKind
  position: [number, number]
  fields: Record<string, unknown>
  script: string | null
  custom: boolean
  /** The node's name, for any kind (16.10.26m §1): its file name and its word in the code; `null` means the handle. */
  script_name: string | null
  /** The script's setting values, by param name. Always `{}` without a script. */
  params: ParamValues
}

export interface FlowEdge {
  from: string
  to: string
}

export interface FlowDefinition {
  schema: 2
  nodes: FlowNode[]
  edges: FlowEdge[]
}

/** Backend 2.10.26e §5: node handle → the object the flow's apply made. */
export type FlowObjects = Record<string, { id: string }>

export interface FlowDraft {
  revision: number
  draft_version: number
  definition: FlowDefinition
  objects: FlowObjects
}

export interface FlowActive {
  revision: number
  definition: FlowDefinition
  objects: FlowObjects
  activated_at: string
}

export interface FlowItem {
  id: string
  name: string
  created_at: string
  updated_at: string
  /** `null` with no draft, or when the stored definition fails the shape check (an older build: "reload"). */
  draft: FlowDraft | null
  active: FlowActive | null
}

export interface FlowSummary {
  id: string
  name: string
  created_at: string
  updated_at: string
  has_draft: boolean
  active_revision: number | null
  node_count: number
}

/** What the editors work on: the draft if there is one, else the active revision. */
export interface WorkingFlow {
  name: string
  definition: FlowDefinition
  objects: FlowObjects
  /** `0` means "the next `PUT` inserts a new draft" (Backend 2.10.26e §4). */
  draftVersion: number
}

/**
 * The one way an editor sets a script, so `custom === (script !== null)` holds by construction. Removing it
 * drops its values (8.10.26f §5) but keeps the name, which is the node's, not its script's (16.10.26m §1).
 */
export function withScript(node: FlowNode, script: string | null): FlowNode {
  if (script === null) return { ...node, script, custom: false, params: {} }
  return { ...node, script, custom: true }
}

/** Any node (16.10.26m §1). `''` and the node's own handle store `null` (Backend 8.10.26e §7: same name, fewer bytes). */
export function withScriptName(node: FlowNode, name: string | null): FlowNode {
  return { ...node, script_name: name === null || name === '' || name === node.id ? null : name }
}

/** `undefined` deletes; a new key on a node already storing `MAX_PARAMS` is refused (the node comes back unchanged). */
export function withParam(node: FlowNode, name: string, value: ParamValue | undefined): FlowNode {
  if (node.script === null) return node
  if (value === undefined) {
    const rest = { ...node.params }
    delete rest[name]
    return { ...node, params: rest }
  }
  const isNew = !Object.prototype.hasOwnProperty.call(node.params, name)
  if (isNew && Object.keys(node.params).length >= MAX_PARAMS) return node
  return { ...node, params: { ...node.params, [name]: value } }
}

/** The one shape both editors dispatch for a script edit (8.10.26f §5). */
export type ScriptChange =
  | { type: 'script'; script: string | null }
  | { type: 'script_name'; name: string | null }
  | { type: 'param'; name: string; value: ParamValue | undefined }
  // ADR 15.10.26n §5: one action, so the node never holds fields that disagree with its script.
  | { type: 'restore_fields'; fields: Record<string, unknown> }

/** ADR 15.10.26n §5: the script's literals become the fields, and the script goes, in one act. */
export function withRestoredFields(node: FlowNode, fields: Record<string, unknown>): FlowNode {
  return { ...withScript(node, null), fields }
}

/** A node's name, for any kind: the file name and the word its code is written with (16.10.26m §1). */
export function scriptNameOf(node: FlowNode): string {
  return node.script_name ?? node.id
}

/**
 * Why `text` cannot be `handle`'s name: `'invalid'`, `'taken'` (another
 * node's handle, scripted or not, or another node's explicit name — stricter
 * than Backend 8.10.26e on purpose), or `null`.
 */
export function scriptNameProblem(def: FlowDefinition, handle: string, text: string): 'invalid' | 'taken' | null {
  if (text === '' || text === handle) return null
  if (!PARAM_NAME.test(text)) return 'invalid'
  const others = def.nodes.filter((node) => node.id !== handle)
  return others.some((node) => node.id === text || node.script_name === text) ? 'taken' : null
}

/** The smallest `n<k>` no node uses as handle or name and `reserved` lacks (8.10.26f §5; 16.10.26m §2). */
export function nextHandle(def: FlowDefinition, reserved: ReadonlySet<string> = new Set()): string {
  const used = new Set(def.nodes.flatMap((node) => (node.script_name === null ? [node.id] : [node.id, node.script_name])))
  let k = 1
  while (used.has(`n${k}`) || reserved.has(`n${k}`)) k += 1
  return `n${k}`
}

/** JSON with object keys sorted, so two equal values always print the same. */
export function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`
  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

/**
 * `stableJson` with every position at `[0, 0]`: keeps `script_name`, `params`
 * and edge order (not `definitionKey`). The Code tab's `basis` (15.10.26m §4);
 * moved here from `connectionSync.ts` so `restore` (16.10.26l §3) reads it
 * without reaching into the editor chunk.
 */
export function layoutFreeKey(def: FlowDefinition): string {
  return stableJson({ ...def, nodes: def.nodes.map((node) => ({ ...node, position: [0, 0] })) })
}

/**
 * The staleness key: nodes (id, kind, fields, script, params) and edges,
 * **without `position` or `script_name`**. Dragging a node or renaming its
 * script clears no test report and no dry-run verdict; changing a field, a
 * script, a setting or an edge does.
 */
export function definitionKey(def: FlowDefinition): string {
  const nodes = def.nodes.map(({ id, kind, fields, script, params }) => ({ id, kind, fields, script, params }))
  return stableJson({ nodes, edges: def.edges })
}

/** Kahn's algorithm, ties broken by node order, so a plan comes out the same every time. `null` on a cycle. */
export function topoOrder(def: FlowDefinition): FlowNode[] | null {
  const indegree = new Map(def.nodes.map((node) => [node.id, 0]))
  for (const edge of def.edges) indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1)
  const order: FlowNode[] = []
  const done = new Set<string>()
  while (order.length < def.nodes.length) {
    const next = def.nodes.find((node) => !done.has(node.id) && indegree.get(node.id) === 0)
    if (next === undefined) return null
    order.push(next)
    done.add(next.id)
    for (const edge of def.edges) {
      if (edge.from === next.id) indegree.set(edge.to, (indegree.get(edge.to) ?? 0) - 1)
    }
  }
  return order
}

/** The direct upstream nodes of `handle`, in node order. */
export function parentsOf(def: FlowDefinition, handle: string): FlowNode[] {
  const from = new Set(def.edges.filter((edge) => edge.to === handle).map((edge) => edge.from))
  return def.nodes.filter((node) => from.has(node.id))
}

/** A dependent node's client: its one direct upstream `client` node, or `null` for zero or two. */
export function clientHandleOf(def: FlowDefinition, handle: string): string | null {
  const clients = parentsOf(def, handle).filter((node) => node.kind === 'client')
  return clients.length === 1 ? clients[0].id : null
}

/** Only the entries of `objects` whose handle is a node of `def`. */
export function onlyNodes(objects: FlowObjects, def: FlowDefinition): FlowObjects {
  const handles = new Set(def.nodes.map((node) => node.id))
  return Object.fromEntries(Object.entries(objects).filter(([handle]) => handles.has(handle)))
}

/** The active record overlaid by the draft's (draft wins), kept to nodes of the working definition. */
export function recordedObjects(flow: FlowItem): FlowObjects {
  const definition = flow.draft?.definition ?? flow.active?.definition
  if (definition === undefined) return {}
  return onlyNodes({ ...flow.active?.objects, ...flow.draft?.objects }, definition)
}

export function workingFlow(flow: FlowItem): WorkingFlow | null {
  const definition = flow.draft?.definition ?? flow.active?.definition
  if (definition === undefined) return null
  return { name: flow.name, definition, objects: recordedObjects(flow), draftVersion: flow.draft?.draft_version ?? 0 }
}

/** C5 offers a recipe only when every kind it uses is on (roadmap D7). */
export function kindAvailable(flags: FeatureFlags | null, kind: NodeKind): boolean {
  if (flags?.enable_oauth_clients !== true) return false
  if (kind === 'webhook') return flags.enable_webhooks === true
  if (kind === 'app') return flags.enable_apps === true
  return true
}

type Wire = Record<string, unknown>

function objectOf(value: unknown): Wire | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Wire) : null
}

/** 8.10.26f §5: values only beside a script; a name on any node (16.10.26m §1); a malformed name reads `null`. */
function scriptFieldsOf(row: Wire, script: string | null): Pick<FlowNode, 'script_name' | 'params'> {
  const name = typeof row.script_name === 'string' && PARAM_NAME.test(row.script_name) ? row.script_name : null
  return { script_name: name, params: script === null ? {} : narrowParamValues(row.params) }
}

function narrowNode(value: unknown): FlowNode | null {
  const row = objectOf(value)
  if (row === null || typeof row.id !== 'string' || !NODE_KINDS.includes(row.kind as NodeKind)) return null
  const position = Array.isArray(row.position) && row.position.length === 2 ? row.position : [0, 0]
  const script = typeof row.script === 'string' ? row.script : null
  return {
    id: row.id,
    kind: row.kind as NodeKind,
    position: [Number(position[0]) || 0, Number(position[1]) || 0],
    fields: objectOf(row.fields) ?? {},
    script,
    custom: script !== null,
    ...scriptFieldsOf(row, script),
  }
}

/** The schemas this build reads; it always writes the last (8.10.26f §5). */
const READABLE_SCHEMAS: readonly unknown[] = [1, 2]

function narrowEdges(value: unknown): FlowEdge[] | null {
  const rows = Array.isArray(value) ? value.map(objectOf) : []
  const edges = rows.flatMap((row) =>
    typeof row?.from === 'string' && typeof row.to === 'string' ? [{ from: row.from, to: row.to }] : [],
  )
  return edges.length === rows.length ? edges : null
}

/** §2's shape check: `null` unless the engine could run it. Schema 1 or 2 in, always 2 out (8.10.26f §5). */
export function narrowDefinition(value: unknown): FlowDefinition | null {
  const row = objectOf(value)
  if (row === null || !READABLE_SCHEMAS.includes(row.schema) || !Array.isArray(row.nodes)) return null
  const nodes = row.nodes.map(narrowNode)
  const edges = narrowEdges(row.edges ?? [])
  if (edges === null || nodes.some((node) => node === null)) return null
  const def: FlowDefinition = { schema: 2, nodes: nodes as FlowNode[], edges }
  const handles = new Set(def.nodes.map((node) => node.id))
  if (handles.size !== def.nodes.length) return null
  if (edges.some((edge) => !handles.has(edge.from) || !handles.has(edge.to))) return null
  return topoOrder(def) === null ? null : def
}
